import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Refresh, ArrowForward, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchSupportTickets,
  createSupportTicket,
  updateSupportTicket,
  updateTicketStatus,
  fetchTenants,
} from '../../../store/slices/superAdminSlice';
import {
  CreateSupportTicket,
  SupportTicketDto,
  TicketPriorityLabels,
  TicketPriorityColors,
  TicketStatusLabels,
  TicketStatusColors,
  TicketStatus,
} from '../../../types/superAdminTypes';

const MODULE_OPTIONS = ['Attendance', 'Leave', 'Payroll', 'Shift', 'Employee', 'Location', 'Reports', 'Other'];
const emptyForm: CreateSupportTicket = { tenantId: '', module: '', subject: '', description: '', priority: 0 };

const SupportTicketsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { supportTickets, tenants, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [statusFilter, setStatusFilter] = useState<number | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState<SupportTicketDto | null>(null);
  const [form, setForm] = useState<CreateSupportTicket>(emptyForm);

  const fetchData = useCallback(() => {
    dispatch(fetchSupportTickets({
      status: statusFilter !== '' ? statusFilter : undefined,
      priority: priorityFilter !== '' ? priorityFilter : undefined,
      searchTerm: searchTerm || undefined,
    }));
  }, [dispatch, statusFilter, priorityFilter, searchTerm]);

  useEffect(() => {
    fetchData();
    dispatch(fetchTenants({}));
  }, [fetchData, dispatch]);

  const handleOpenCreate = () => {
    setEditingTicket(null);
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const handleOpenEdit = (ticket: SupportTicketDto) => {
    setEditingTicket(ticket);
    setForm({
      tenantId: ticket.tenantId,
      module: ticket.module,
      subject: ticket.subject,
      description: ticket.description,
      priority: ticket.priority,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTicket(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    try {
      if (editingTicket) {
        await dispatch(updateSupportTicket({ id: editingTicket.id, data: { id: editingTicket.id, ...form } })).unwrap();
        toast.success('Ticket updated successfully');
      } else {
        await dispatch(createSupportTicket(form)).unwrap();
        toast.success('Ticket created successfully');
      }

      handleCloseDialog();
      fetchData();
    } catch (err: any) {
      toast.error(err || (editingTicket ? 'Failed to update ticket' : 'Failed to create ticket'));
    }
  };

  const handleStatusChange = async (id: string, newStatus: number) => {
    try {
      await dispatch(updateTicketStatus({ id, data: { id, status: newStatus } })).unwrap();
      toast.success('Ticket status updated');
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Failed to update status');
    }
  };

  const getNextStatus = (current: number): number | null => {
    switch (current) {
      case TicketStatus.Open: return TicketStatus.InProgress;
      case TicketStatus.InProgress: return TicketStatus.Resolved;
      case TicketStatus.Resolved: return TicketStatus.Closed;
      default: return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Support Tickets</Typography>
          <Typography variant="body2" color="text.secondary">Manage support requests from tenants</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
          Create Ticket
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as number | '')}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value={0}>Open</MenuItem>
                <MenuItem value={1}>In Progress</MenuItem>
                <MenuItem value={2}>Resolved</MenuItem>
                <MenuItem value={3}>Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value as number | '')}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value={0}>Low</MenuItem>
                <MenuItem value={1}>Medium</MenuItem>
                <MenuItem value={2}>High</MenuItem>
                <MenuItem value={3}>Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {supportTickets.length}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Ticket #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 8 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : supportTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No tickets found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              supportTickets.map((ticket) => {
                const nextStatus = getNextStatus(ticket.status);
                return (
                  <TableRow key={ticket.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{ticket.ticketNumber}</Typography></TableCell>
                    <TableCell>{ticket.tenantName}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>{ticket.module}</TableCell>
                    <TableCell>
                      <Chip label={TicketPriorityLabels[ticket.priority] || 'Unknown'} color={TicketPriorityColors[ticket.priority] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={TicketStatusLabels[ticket.status] || 'Unknown'} color={TicketStatusColors[ticket.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(ticket)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {nextStatus !== null && (
                        <Tooltip title={`Move to ${TicketStatusLabels[nextStatus]}`}>
                          <IconButton size="small" color="primary" onClick={() => handleStatusChange(ticket.id, nextStatus)}>
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingTicket ? 'Edit Support Ticket' : 'Create Support Ticket'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select value={form.tenantId} label="Tenant" onChange={(e) => setForm({ ...form, tenantId: e.target.value })}>
                  {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.companyName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Module</InputLabel>
                <Select value={form.module} label="Module" onChange={(e) => setForm({ ...form, module: e.target.value })}>
                  {MODULE_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={form.priority} label="Priority" onChange={(e) => setForm({ ...form, priority: e.target.value as number })}>
                  <MenuItem value={0}>Low</MenuItem>
                  <MenuItem value={1}>Medium</MenuItem>
                  <MenuItem value={2}>High</MenuItem>
                  <MenuItem value={3}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" multiline rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {editingTicket ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTicketsPage;
